// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## progressTracker
//
// Track proress & times for various skills, report back to a callback function.
import type { UPracticeData } from 'shared/definitions/progress-data';
import * as Skills from './skills';
import utils from '../utils';
import type { RhythmSkillAccuracy } from '../uTheory.Interaction/rhythmChecker';

interface RunningStats {
  percent: number;
  attempts: number;
  rightAnswers: number;
  totalTimeMS?: number;
  averageTimeMS?: number;
  speedPercent?: number;
}

class ProgressTracker {
  resultsCallbacks: Function[] = [];

  /** array of skillIds in numerical format * */
  skillsList: number[] = [];

  data: UPracticeData[] = [];

  answerAttempts = 0;

  rightAnswers = 0;

  /** when the current question was started, UTC milliseconds since 1/1/1970 */
  startTime: number;

  /** when the first question was started, UTC milliseconds since 1/1/1970 */
  birthTime: number;

  /** whether the interactive has been started */
  started = false;

  /** Target speed in seconds, used for getting a running average */
  targetSpeed?: number;

  /**
   * Start a new question, reset this.startTime, set birthTime & started if needed.
   * @param targetSpeed in seconds, used for getting a running average
   */
  start(targetSpeed?) {
    const now = new Date();
    this.startTime = now.getTime();
    this.birthTime = this.birthTime || now.getTime();
    this.started = true;
    this.targetSpeed = targetSpeed;
  }

  /**
   * Calculate time for the current question, and reset startTime if requested (as
   * after answering a question correctly). Returns time in MS since start of current
   * question (before resetting startTime).
   * @param resetStartTime boolean
   */
  private calculateTime(resetStartTime = true) {
    const now = new Date();
    const currentTime = now.getTime();
    const duration = currentTime - this.startTime;
    if (resetStartTime) this.startTime = currentTime;
    return duration;
  }

  /**
   * Search the skillsList & data arrays to find the index of a skillId
   * @param skillId
   */
  private getOrMakeSkillIndex(skillId: number) {
    // skillId has already been converted to a number -- and if not, we should throw an error
    if (!Number.isFinite(skillId)) {
      throw new Error(`getOrMakeSkill requires a finite number, not ${skillId}`);
    }

    // We've theoretically already verified that the skill actually exists, if not, throw an error
    const skillDef = Skills.getSkill(skillId);
    if (!skillDef) {
      throw new Error(
        `${skillId} is not a valid skillId. getOrMakeSkillIndex requires a valid numeric skillId`
      );
    }

    // If it's a timed skill, we need to save each question:
    const timedSkill = skillDef.master.speed;

    let index = this.skillsList.indexOf(skillId);

    if (index === -1) {
      index = this.skillsList.length;
      this.skillsList.push(skillId);
      const newDataObj: UPracticeData = {
        skill: skillId,
        questions: 0,
        rightAnswers: 0,
        wrongAnswers: 0,
        totalTime: 0,
        averageTime: 0,
        percent: 0,
      };
      if (timedSkill) newDataObj.questionData = [];
      this.data.push(newDataObj);
    }
    return index;
  }

  /**
   * Returns stats for this moment, for a running display of average speed, accuracy, etc...
   * @returns {Object} { percent, rightAnswers, attempts, totalTimeMS, averageTimeMS, speedPercent }
   */
  getRunningStats(): RunningStats {
    const stats: RunningStats = {
      percent: (100 * this.rightAnswers) / (this.answerAttempts || 1),
      attempts: this.answerAttempts,
      rightAnswers: this.rightAnswers,
    };

    if (this.birthTime) {
      const totalTimeMS = new Date().getTime() - this.birthTime;
      stats.totalTimeMS = totalTimeMS;
      stats.averageTimeMS = totalTimeMS / (this.answerAttempts || 1);

      if (this.targetSpeed) {
        stats.speedPercent = Math.min((this.targetSpeed * 1000) / stats.averageTimeMS, 1) * 100;
      }
    }
    return stats;
  }

  /**
   *
   * @param {Object|array} skillsIds array of skillIds in numerical or string format
   * @param {boolean} rightAnswer is the answer corret?
   * @param {boolean} gameOver was this the last question?
   */
  private answer(skillIdOrIds: (number | string) | (number | string)[], rightAnswer, gameOver) {
    const questionTime = this.calculateTime(rightAnswer);
    this.answerAttempts++;
    if (rightAnswer) this.rightAnswers++;

    const skillIds = Array.isArray(skillIdOrIds) ? skillIdOrIds : [skillIdOrIds];

    skillIds.forEach((skillName) => {
      const skillDef = Skills.getSkill(skillName);
      // Sanity check to prevent data corruption:
      if (!skillDef) {
        return;
      }
      const skillId = skillDef.id;

      // If it's a timed skill, we need to save each question:
      const timedSkill = skillDef.master.speed;

      const index = this.getOrMakeSkillIndex(skillId);
      const skill = this.data[index];

      if (timedSkill) {
        skill.questionData.push({
          time: questionTime,
          percent: rightAnswer ? 100 : 0,
          numQuestions: 1,
        });
      }
      skill.questions++;
      if (rightAnswer) skill.rightAnswers++;
      else skill.wrongAnswers++;
      skill.totalTime += questionTime;
      skill.averageTime = skill.totalTime / skill.questions;
      skill.percent = (skill.rightAnswers * 100) / skill.questions;
    });
    if (gameOver) this.reportData();
  }

  rhythmAnswer(skillAccuracies: RhythmSkillAccuracy[], gameOver?: boolean, saveNow?: boolean) {
    skillAccuracies.forEach((skillAccuracy) => {
      this.answerAttempts += skillAccuracy.questions;
      this.rightAnswers += (skillAccuracy.questions * skillAccuracy.average) / 100;

      const skillIntID = Skills.makeInt(skillAccuracy.textId);
      // Sanity check to prevent data corruption:
      if (typeof skillIntID === 'undefined') {
        return;
      }
      const index = this.getOrMakeSkillIndex(skillIntID);
      const skill = this.data[index];
      skill.questions += skillAccuracy.questions;
      skill.rightAnswers += (skillAccuracy.questions * skillAccuracy.average) / 100;
      const wrongAnswers =
        skillAccuracy.questions - (skillAccuracy.questions * skillAccuracy.average) / 100;
      skill.wrongAnswers += wrongAnswers;
      skill.percent = (skill.rightAnswers * 100) / skill.questions;
      delete skill.totalTime;
      delete skill.averageTime;
    });
    if (gameOver) return this.reportData();
    if (saveNow) return this.reportData(true);
    return undefined;
  }

  /**
   *
   * @param {number} questions
   * @param {boolean} gameOver
   */
  multipleAnswers(
    skillAnswerObjects: {
      /** skill ID in string or number format */
      skill: string | number;
      /** number of right answers */
      right: number;
      /** number of wrong answers */
      wrong: number;
      /** number of questions total */
      questions: number;
    }[],
    questions: number,
    gameOver?: boolean
  ) {
    // console.log(skillAnswerObjects);
    /*
    array: [
      { skill: #, questions: total, right: total, wrong: total },
      {}, etc...
    ]
    */
    let questionTime = this.calculateTime();
    if (typeof questions === 'undefined') return;
    questionTime /= questions;

    const averager = utils.averageFactory();
    skillAnswerObjects.forEach((skillAnswers) =>
      averager(skillAnswers.right / skillAnswers.questions, skillAnswers.questions)
    );
    const average = averager(100, 0);
    this.answerAttempts += questions;
    this.rightAnswers += average * questions;

    skillAnswerObjects.forEach((skillAnswers) => {
      const skillId = Skills.makeInt(skillAnswers.skill);
      // Sanity check to prevent data corruption
      if (typeof skillId === 'undefined') {
        return;
      }
      skillAnswers.skill = skillId;
      const index = this.getOrMakeSkillIndex(skillAnswers.skill);
      const skill = this.data[index];
      skill.questions += skillAnswers.questions;
      skill.rightAnswers += skillAnswers.right;
      skill.wrongAnswers += skillAnswers.wrong;
      skill.totalTime += questionTime * skillAnswers.questions;
      skill.averageTime = skill.totalTime / skill.questions;
      skill.percent = (skill.rightAnswers * 100) / skill.questions;
    });

    if (gameOver) this.reportData();
  }

  rightAnswer(skillIdOrIds: (number | string) | (number | string)[], gameOver?: boolean) {
    this.answer(skillIdOrIds, true, gameOver);
  }

  wrongAnswer(skillIdOrIds: (number | string) | (number | string)[], gameOver?: boolean) {
    this.answer(skillIdOrIds, false, gameOver);
  }

  addResultsCallback(callBack: Function, thisArg) {
    if (typeof thisArg !== 'undefined') {
      this.resultsCallbacks.push(callBack.bind(thisArg));
    } else this.resultsCallbacks.push(callBack);
  }

  reportData(saveBeforeGameIsOver = false) {
    this.resultsCallbacks.forEach((callback) => {
      callback(this.data, saveBeforeGameIsOver);
    });
    // GCR TODO: Disabled because front-end can't yet handle multiple saves.
    /*
    if (false && saveBeforeGameIsOver) {
      // Reset data, since prior data will have already been saved.
      this.data = [];
      this.skillsList = [];
    }
*/
  }

  getData() {
    return this.data;
  }

  toString() {
    let text = 'Stats:';
    this.data.forEach((datum) => {
      text += `\nskill: ${datum.skill}`;
      text += `\nquestions: ${datum.questions}`;
      text += `\nrightAnswers: ${datum.rightAnswers}`;
      text += `\nwrongAnswers: ${datum.wrongAnswers}`;
      text += `\ntotalTime: ${datum.totalTime}`;
      text += `\naverageTime: ${datum.averageTime}`;
      text += `\npercent: ${datum.percent}`;
    }, this);
    return text;
  }
}

export default ProgressTracker;
